import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

import "./HomeNoticeBanner.css";

import EcoIcon from "../eco-icon/EcoIcon";
import {
  listNotificationsApi,
  markNotificationReadApi,
  NOTIFICATIONS_CHANGED_EVENT,
  notifyNotificationsChanged,
  StaffNotification,
} from "../../api/notificationsApi";

/** Linhas do texto mostradas no destaque; o resto fica no "Ver agora". */
const MAX_LINES = 3;

/**
 * Destaque do aviso não lido mais recente na home (pedido do usuário,
 * 22/10/2026): o sino da Topbar ficou discreto demais. Some quando não há
 * aviso pendente. "Ver agora" e "Dispensar" marcam como lido, e o sino se
 * atualiza pelo evento `NOTIFICATIONS_CHANGED_EVENT`.
 */
export default function HomeNoticeBanner() {
  const navigate = useNavigate();
  const [items, setItems] = useState<StaffNotification[]>([]);
  const [total, setTotal] = useState(0);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const page = await listNotificationsApi({ unread: true, limit: 1 });
      setItems(page.items);
      setTotal(page.total);
    } catch {
      // Falha aqui não atrapalha a home: o destaque só não aparece.
      setItems([]);
      setTotal(0);
    }
  }, []);

  useEffect(() => {
    load();
    window.addEventListener(NOTIFICATIONS_CHANGED_EVENT, load);
    return () => window.removeEventListener(NOTIFICATIONS_CHANGED_EVENT, load);
  }, [load]);

  const notice = items[0];
  if (!notice) return null;

  const lines = (notice.body ?? "").split("\n").filter(Boolean);
  const shown = lines.slice(0, MAX_LINES);
  const hidden = lines.length - shown.length;
  const others = total - 1;

  const markRead = async () => {
    setBusy(true);
    try {
      await markNotificationReadApi(notice.id);
    } catch {
      // Se não marcou, a próxima carga mostra de novo.
    } finally {
      setBusy(false);
      notifyNotificationsChanged();
    }
  };

  const open = async () => {
    await markRead();
    if (notice.link) navigate(notice.link);
  };

  return (
    <section className="homeNotice" role="status" aria-live="polite" aria-label="Aviso não lido">
      <span className="homeNotice__icon" aria-hidden="true">
        <EcoIcon name="bell" size={24} />
        <span className="homeNotice__pulse" />
      </span>

      <div className="homeNotice__content">
        <span className="homeNotice__eyebrow">
          Aviso para você{others > 0 ? ` · mais ${others} no sino` : ""}
        </span>
        <strong className="homeNotice__title">{notice.title}</strong>
        {shown.length ? (
          <ul className="homeNotice__lines">
            {shown.map((line, i) => (
              <li key={i}>{line}</li>
            ))}
            {hidden > 0 ? <li className="homeNotice__more">e mais {hidden}</li> : null}
          </ul>
        ) : null}
      </div>

      <div className="homeNotice__actions">
        <button type="button" className="homeNotice__btn homeNotice__btn--primary" onClick={open} disabled={busy}>
          Ver agora
          <EcoIcon name="arrowRight" size={16} />
        </button>
        <button type="button" className="homeNotice__btn" onClick={markRead} disabled={busy}>
          Dispensar
        </button>
      </div>
    </section>
  );
}
